import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { AddItemButton } from './AddItemButton';
import { AddTimelineEventDataMenu } from './AddTimelineEventDataMenu';
import { TimelineEmptyState } from './TimelineEmptyState';
import { AddTimelineEventModal, TIMELINE_ITEM_TYPE_VALUES } from './AddTimelineEventModal';
import { ImportTimelineModal } from './ImportTimelineModal';
import { SaveTimelineViewModal, type TimelineViewState } from './SaveTimelineViewModal';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { areTimelineViewStatesEqual } from '../utils/timeline-view-state';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ClearFiltersButton } from './ClearFiltersButton';
import { ConfirmDialog } from './ConfirmDialog';
import { DataTableSelectionActions } from './DataTableSelectionActions';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { Tabs } from './ui/tabs';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { ViewSwitcher, useViewModeSearchParam } from './ViewSwitcher';
import { ChevronDown, ListFilter, ListFilterPlus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RowSelectionState } from '@tanstack/react-table';
import 'vis-timeline/styles/vis-timeline-graph2d.css';
import './scribane-timeline.css';
import { formatTimelineDate } from '../utils/timeline-date-format';
import {
  applyTimelineDetailLevelToItems,
  type TimelineDetailLevel,
} from '../utils/timeline-detail-level';
import type { TimelineNavigationApi } from '../utils/timeline-navigation';
import { TimelineNavigationControls } from './TimelineNavigationControls';
import { TimelineItemsTable } from './TimelineItemsTable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { useIsMobile } from '../hooks/use-mobile';
import { shouldShowHeaderCreateActions } from '../utils/header-create-actions';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { toBcp47Locale } from '../locales/locale';
import {
  applyTagAssignment,
  diffIds,
  intersectIds,
} from '../utils/resource-bulk-assign';
import { applyDocumentAssignment } from '../utils/timeline-document-assign';
import {
  timelineItemTypeClassName,
  timelineItemTypeSwatchColor,
} from '../utils/timeline-item-type-colors';
import type { EntityMentionOption } from '../utils/entity-mentions';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import {
  buildReferencedInByTargetId,
  type EntityReferenceEdge,
} from '../utils/related-entities';
import type { MentionableEntity } from './RichTextField';

type ViewMode = 'timeline' | 'table';

type GroupByMode = 'none' | 'type' | 'tag' | 'chapter' | 'storyline' | 'scene' | 'document';

const TIMELINE_DETAIL_LEVEL_UNSET = '__unset__';

const MAX_TIMELINE_TITLE_LENGTH = 35;

function truncateTimelineTitle(title: string): string {
  if (title.length <= MAX_TIMELINE_TITLE_LENGTH) return title;
  return title.slice(0, MAX_TIMELINE_TITLE_LENGTH - 1).trim() + '…';
}

function stripHtmlToText(html: string): string {
  if (!html?.trim()) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
}

const ITEM_TYPE_LABEL_KEY: Record<string, string> = {
  historical_event: 'timeline_item_type_historical_event',
  fictional_event: 'timeline_item_type_fictional_event',
  planning: 'timeline_item_type_planning',
  other: 'timeline_item_type_other',
  auto_historical: 'timeline_item_type_auto_historical',
  auto_fictional: 'timeline_item_type_auto_fictional',
};

interface TimelineItemApi {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  item_type: string;
  tags: string[];
  start_date: string;
  start_precision: string;
  start_timezone?: string | null;
  end_date: string | null;
  end_precision: string | null;
  end_timezone?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TimelineViewItem {
  id: string;
  name: string;
}

function isPlanningRangeItem(item: TimelineItemApi): boolean {
  return item.item_type === 'planning' && Boolean(item.end_date?.trim());
}

function visItemExtras(item: TimelineItemApi): { type?: 'background'; className: string } {
  const className = timelineItemTypeClassName(item.item_type);
  return isPlanningRangeItem(item)
    ? { type: 'background' as const, className }
    : { className };
}

export const ProjectTimeline: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewIdParam = searchParams.get('viewId');
  const timelineItemIdParam = searchParams.get('timelineItemId');
  const { token } = useAuth();
  const { t, locale } = useLocale();
  const { canEditProject } = useProjectAccess();
  const { showToast, showErrorToast } = useToast();
  const isMobile = useIsMobile();
  const canEditProjectRef = useRef(canEditProject);
  canEditProjectRef.current = canEditProject;
  const [items, setItems] = useState<TimelineItemApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [autoExtractDocId, setAutoExtractDocId] = useState('');
  const [autoExtracting, setAutoExtracting] = useState(false);
  const [autoExtractConfirmOpen, setAutoExtractConfirmOpen] = useState(false);
  const [autoFreshnessWarn, setAutoFreshnessWarn] = useState<string | null>(null);
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [timelineViews, setTimelineViews] = useState<TimelineViewItem[]>([]);
  const [activeTimelineViewName, setActiveTimelineViewName] = useState<string | null>(null);
  const [loadedViewState, setLoadedViewState] = useState<TimelineViewState | null>(null);
  const [updatingTimelineView, setUpdatingTimelineView] = useState(false);
  const preserveStateOnViewClearRef = useRef(false);
  const previousTimelineViewIdRef = useRef<string | null>(null);
  const [editItem, setEditItem] = useState<TimelineItemApi | null>(null);
  const [hoveredItem, setHoveredItem] = useState<{
    itemId: string;
    position: { top: number; left: number };
  } | null>(null);
  const [textFilter, setTextFilter] = useState('');
  const [selectedItemTypes, setSelectedItemTypes] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [excludedTags, setExcludedTags] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupByMode>('none');
  const [groupByTypes, setGroupByTypes] = useState<Set<string>>(new Set());
  const [groupByTags, setGroupByTags] = useState<Set<string>>(new Set());
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [selectedGroupByDocuments, setSelectedGroupByDocuments] = useState<Set<string>>(new Set());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pendingDeleteTimelineItemIds, setPendingDeleteTimelineItemIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const assignQueueRef = useRef(Promise.resolve());
  const [entityRefs, setEntityRefs] = useState<EntityReferenceEdge[]>([]);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [chapterGroups, setChapterGroups] = useState<Array<{ id: string; title: string; documentId: string; documentTitle: string; order: number }>>([]);
  const [storylineGroups, setStorylineGroups] = useState<Array<{ id: string; title: string; documentId: string; documentTitle: string }>>([]);
  const [sceneGroups, setSceneGroups] = useState<Array<{ id: string; title: string; documentId: string; documentTitle: string; order: number }>>([]);
  const [projectDocuments, setProjectDocuments] = useState<Array<{ id: string; title: string }>>([]);
  const [detailScrollToSection, setDetailScrollToSection] = useState<
    'related_entities' | undefined
  >(undefined);
  const [viewMode, setViewMode] = useViewModeSearchParam<ViewMode>({
    allowedModes: ['timeline', 'table'] as const,
    defaultMode: 'timeline',
  });
  const [detailLevel, setDetailLevel] = useState<TimelineDetailLevel | null>(null);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineFrameRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<TimelineNavigationApi | null>(null);
  const initialTimelineWindowRef = useRef<{ start: string; end: string } | null>(null);
  const [timelineWindow, setTimelineWindow] = useState<{ start: string; end: string } | null>(null);
  const timelineWindowRef = useRef<{ start: string; end: string } | null>(null);
  timelineWindowRef.current = timelineWindow;
  const [savedTimelineWindow, setSavedTimelineWindow] = useState<{ start: string; end: string } | null>(null);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const lastEventForDefaults = useMemo((): TimelineItemApi | null => {
    if (items.length === 0) return null;
    return items.reduce((latest, item) =>
      new Date(item.createdAt).getTime() > new Date(latest.createdAt).getTime() ? item : latest
    );
  }, [items]);

  const uniqueTypes = useMemo(() => [...new Set(items.map((i) => i.item_type))].sort(), [items]);
  const uniqueTags = useMemo(
    () => [...new Set(items.flatMap((i) => i.tags))].filter(Boolean).sort(),
    [items]
  );

  const uniqueDocuments = useMemo(() => {
    return [...projectDocuments].sort((a, b) => a.title.localeCompare(b.title));
  }, [projectDocuments]);

  const itemDocumentIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const ref of entityRefs) {
      if (ref.sourceType !== 'timeline_item') continue;
      if (ref.targetDocumentId) {
        const existing = map.get(ref.sourceId) ?? new Set<string>();
        existing.add(ref.targetDocumentId);
        map.set(ref.sourceId, existing);
      }
      if (ref.targetType === 'document') {
        const existing = map.get(ref.sourceId) ?? new Set<string>();
        existing.add(ref.targetId);
        map.set(ref.sourceId, existing);
      }
    }
    return map;
  }, [entityRefs]);

  const itemLinkedDocumentIds = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const ref of entityRefs) {
      if (ref.sourceType !== 'timeline_item' || ref.targetType !== 'document') continue;
      const existing = map.get(ref.sourceId) ?? [];
      existing.push(ref.targetId);
      map.set(ref.sourceId, existing);
    }
    return map;
  }, [entityRefs]);

  const documentOptionsById = useMemo(() => {
    const map = new Map<string, EntityMentionOption>();
    for (const doc of projectDocuments) {
      map.set(doc.id, { id: doc.id, name: doc.title });
    }
    return map;
  }, [projectDocuments]);

  const hasTimelinePositionChange = useMemo(() => {
    if (viewMode !== 'timeline') return false;
    if (!timelineWindow) return false;
    if (!savedTimelineWindow) return true;
    return (
      timelineWindow.start !== savedTimelineWindow.start || timelineWindow.end !== savedTimelineWindow.end
    );
  }, [viewMode, timelineWindow, savedTimelineWindow]);

  const canSaveView = useMemo(
    () =>
      textFilter.trim() !== '' ||
      selectedItemTypes.size > 0 ||
      selectedTags.size > 0 ||
      excludedTags.size > 0 ||
      selectedDocumentId !== '' ||
      groupBy !== 'none' ||
      detailLevel !== null ||
      hasTimelinePositionChange,
    [
      textFilter,
      selectedItemTypes,
      selectedTags,
      excludedTags,
      selectedDocumentId,
      groupBy,
      detailLevel,
      hasTimelinePositionChange,
    ]
  );

  const hasLoadedTimelineView = Boolean(viewIdParam);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (textFilter.trim() !== '') count += 1;
    count += selectedItemTypes.size;
    count += selectedTags.size;
    count += excludedTags.size;
    if (selectedDocumentId !== '') count += 1;
    if (groupBy !== 'none') count += 1;
    return count;
  }, [textFilter, selectedItemTypes, selectedTags, excludedTags, selectedDocumentId, groupBy]);

  const clearFilters = useCallback((): void => {
    setTextFilter('');
    setSelectedItemTypes(new Set());
    setSelectedTags(new Set());
    setExcludedTags(new Set());
    setSelectedDocumentId('');
    setGroupBy('none');
    setGroupByTypes(new Set());
    setGroupByTags(new Set());
    setSelectedGroupByDocuments(new Set());
  }, []);

  const timelineViewOptions = useMemo(
    () => timelineViews.map((view) => ({ value: view.id, label: view.name })),
    [timelineViews]
  );

  const currentViewState = useMemo(
    (): TimelineViewState => ({
      textFilter,
      selectedItemTypes: [...selectedItemTypes],
      selectedTags: [...selectedTags],
      excludedTags: [...excludedTags],
      selectedDocumentId,
      groupBy,
      groupByTypes: [...groupByTypes],
      groupByTags: [...groupByTags],
      groupByDocuments: [...selectedGroupByDocuments],
      viewMode,
      detailLevel,
      ...(viewMode === 'timeline' && timelineWindow && { timelineWindow }),
    }),
    [
      textFilter,
      selectedItemTypes,
      selectedTags,
      excludedTags,
      selectedDocumentId,
      groupBy,
      groupByTypes,
      groupByTags,
      selectedGroupByDocuments,
      viewMode,
      detailLevel,
      timelineWindow,
    ]
  );

  const isLoadedTimelineViewDirty = useMemo(() => {
    if (!hasLoadedTimelineView || !loadedViewState) return false;
    return !areTimelineViewStatesEqual(currentViewState, loadedViewState);
  }, [hasLoadedTimelineView, loadedViewState, currentViewState]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (textFilter.trim()) {
      list = list.filter((item) => {
        const nameMatch = textIncludesQuery(item.name, textFilter);
        const descText = stripHtmlToText(item.description ?? '');
        const descMatch = textIncludesQuery(descText, textFilter);
        return nameMatch || descMatch;
      });
    }
    if (selectedItemTypes.size > 0) {
      list = list.filter((item) => selectedItemTypes.has(item.item_type));
    }
    if (selectedTags.size > 0) {
      list = list.filter((item) => item.tags.some((tag) => selectedTags.has(tag)));
    }
    if (excludedTags.size > 0) {
      list = list.filter((item) => !item.tags.some((tag) => excludedTags.has(tag)));
    }
    if (selectedDocumentId) {
      list = list.filter((item) => itemDocumentIds.get(item.id)?.has(selectedDocumentId) ?? false);
    }
    return list;
  }, [items, textFilter, selectedItemTypes, selectedTags, excludedTags, selectedDocumentId, itemDocumentIds]);

  const displayItems = useMemo(
    () => applyTimelineDetailLevelToItems(filteredItems, detailLevel),
    [filteredItems, detailLevel]
  );

  const tableItems = useMemo(() => {
    const referencedInById = buildReferencedInByTargetId(
      'timeline_item',
      null,
      entityRefs,
      mentionableEntities
    );
    return displayItems.map((item) => ({
      ...item,
      referencedIn: referencedInById.get(item.id) ?? [],
    }));
  }, [displayItems, entityRefs, mentionableEntities]);

  const selectedTimelineItemIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection]
  );
  const hasTimelineSelection = selectedTimelineItemIds.length > 0 && viewMode === 'table';

  const requestDeleteSelectedTimelineItems = useCallback((): void => {
    if (selectedTimelineItemIds.length === 0) {
      return;
    }
    setPendingDeleteTimelineItemIds([...selectedTimelineItemIds]);
  }, [selectedTimelineItemIds]);

  const confirmDeleteSelectedTimelineItems = useCallback(async (): Promise<void> => {
    const itemIds = pendingDeleteTimelineItemIds;
    setPendingDeleteTimelineItemIds([]);
    if (itemIds.length === 0 || !projectId || !token) {
      return;
    }

    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        itemIds.map(async (itemId) => {
          const response = await fetch(
            `${API_BASE}/api/projects/${projectId}/timeline-items/${itemId}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
          );
          if (!response.ok) {
            throw new Error(t('timeline_delete_failed'));
          }
          return itemId;
        })
      );
      const deletedIds = results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
        .map((result) => result.value);
      const failed = results.length - deletedIds.length;
      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds);
        setItems((prev) => prev.filter((item) => !deletedSet.has(item.id)));
        setRowSelection({});
        showToast(
          deletedIds.length === 1
            ? t('timeline_item_deleted_success')
            : t('timeline_items_deleted_success').replace('{count}', String(deletedIds.length))
        );
      }
      if (failed > 0) {
        showErrorToast(
          t('timeline_items_delete_partial_failed').replace('{count}', String(failed))
        );
      }
    } catch (err) {
      console.error('Error deleting timeline items:', err);
      showErrorToast(t('timeline_delete_failed'));
    } finally {
      setBulkDeleting(false);
    }
  }, [pendingDeleteTimelineItemIds, projectId, token, t, showToast, showErrorToast]);

  useEffect(() => {
    if (viewMode !== 'table') {
      setRowSelection({});
    }
  }, [viewMode]);

  useEffect(() => {
    const visibleIds = new Set(filteredItems.map((item) => item.id));
    setRowSelection((prev) => {
      let changed = false;
      const next: RowSelectionState = {};
      for (const [id, selected] of Object.entries(prev)) {
        if (selected && visibleIds.has(id)) {
          next[id] = true;
        } else if (selected) {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [filteredItems]);

  useEffect(() => {
    if (!hasTimelineSelection) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setRowSelection({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasTimelineSelection]);

  useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    const fetchTags = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setProjectTags(Array.isArray(data.tags) ? data.tags : []);
        }
      } catch {
        setProjectTags([]);
      }
    };
    void fetchTags();
  }, [projectId, token]);

  const assignmentItemTypes = useMemo(() => {
    if (!hasTimelineSelection) {
      return selectedItemTypes;
    }
    const types = selectedTimelineItemIds.map(
      (id) => items.find((item) => item.id === id)?.item_type ?? ''
    );
    const shared = intersectIds(types.map((type) => (type ? [type] : [])));
    return new Set(shared);
  }, [hasTimelineSelection, selectedTimelineItemIds, items, selectedItemTypes]);

  const assignmentTags = useMemo(() => {
    if (!hasTimelineSelection) {
      return selectedTags;
    }
    const tagLists = selectedTimelineItemIds.map(
      (id) => items.find((item) => item.id === id)?.tags ?? []
    );
    return new Set(intersectIds(tagLists));
  }, [hasTimelineSelection, selectedTimelineItemIds, items, selectedTags]);

  const assignmentDocumentIds = useMemo(() => {
    if (!hasTimelineSelection) {
      return selectedDocumentId ? new Set([selectedDocumentId]) : new Set<string>();
    }
    const docLists = selectedTimelineItemIds.map((id) => itemLinkedDocumentIds.get(id) ?? []);
    return new Set(intersectIds(docLists));
  }, [hasTimelineSelection, selectedTimelineItemIds, itemLinkedDocumentIds, selectedDocumentId]);

  const assignModeClassName = hasTimelineSelection
    ? 'border-[var(--scribane-green)] bg-[rgba(94,204,128,0.08)]'
    : undefined;

  const persistTimelineItemPatch = useCallback(
    async (itemId: string, patch: Record<string, unknown>): Promise<void> => {
      if (!projectId || !token) {
        throw new Error('assign failed');
      }
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-items/${itemId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
      if (!response.ok) {
        throw new Error('assign failed');
      }
    },
    [projectId, token]
  );

  const refreshEntityRefs = useCallback(async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    try {
      const refsRes = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (refsRes.ok) {
        const refsData = await refsRes.json();
        setEntityRefs(refsData.references ?? []);
      }
    } catch {
      // ignore
    }
  }, [projectId, token]);

  const applyBulkTimelineAssignment = useCallback(
    (params: {
      itemIds: string[];
      itemType?: string;
      addedTags?: string[];
      removedTags?: string[];
      addedDocumentIds?: string[];
      removedDocumentIds?: string[];
    }): void => {
      const run = async (): Promise<void> => {
        const addedTags = params.addedTags ?? [];
        const removedTags = params.removedTags ?? [];
        const addedDocumentIds = params.addedDocumentIds ?? [];
        const removedDocumentIds = params.removedDocumentIds ?? [];
        const assigningType = Boolean(params.itemType);
        const assigningTags = addedTags.length > 0 || removedTags.length > 0;
        const assigningDocuments = addedDocumentIds.length > 0 || removedDocumentIds.length > 0;
        if (
          (!assigningType && !assigningTags && !assigningDocuments) ||
          params.itemIds.length === 0
        ) {
          return;
        }

        const previousItems = items;
        const previousRefs = entityRefs;

        setItems((prev) =>
          prev.map((item) => {
            if (!params.itemIds.includes(item.id)) {
              return item;
            }
            let next = item;
            if (assigningType && params.itemType) {
              next = { ...next, item_type: params.itemType };
            }
            if (assigningTags) {
              next = {
                ...next,
                tags: applyTagAssignment(next.tags, addedTags, removedTags),
              };
            }
            if (assigningDocuments) {
              next = {
                ...next,
                description: applyDocumentAssignment({
                  html: next.description,
                  currentDocumentIds: itemLinkedDocumentIds.get(item.id) ?? [],
                  addedDocumentIds,
                  removedDocumentIds,
                  documentOptionsById,
                }),
              };
            }
            return next;
          })
        );

        if (assigningDocuments) {
          setEntityRefs((prev) => {
            let next = prev.filter(
              (ref) =>
                !(
                  ref.sourceType === 'timeline_item' &&
                  params.itemIds.includes(ref.sourceId) &&
                  ref.targetType === 'document' &&
                  removedDocumentIds.includes(ref.targetId)
                )
            );
            for (const itemId of params.itemIds) {
              const current = new Set(
                next
                  .filter(
                    (ref) =>
                      ref.sourceType === 'timeline_item' &&
                      ref.sourceId === itemId &&
                      ref.targetType === 'document'
                  )
                  .map((ref) => ref.targetId)
              );
              for (const docId of addedDocumentIds) {
                if (current.has(docId)) continue;
                next = [
                  ...next,
                  {
                    sourceType: 'timeline_item',
                    sourceId: itemId,
                    sourceDocumentId: null,
                    targetType: 'document',
                    targetId: docId,
                    targetDocumentId: null,
                  },
                ];
              }
            }
            return next;
          });
        }

        try {
          await Promise.all(
            params.itemIds.map(async (itemId) => {
              const item = previousItems.find((entry) => entry.id === itemId);
              if (!item) {
                return;
              }
              const patch: Record<string, unknown> = {};
              if (assigningType && params.itemType) {
                patch.item_type = params.itemType;
              }
              if (assigningTags) {
                patch.tags = applyTagAssignment(item.tags, addedTags, removedTags);
              }
              if (assigningDocuments) {
                patch.description = applyDocumentAssignment({
                  html: item.description,
                  currentDocumentIds: itemLinkedDocumentIds.get(itemId) ?? [],
                  addedDocumentIds,
                  removedDocumentIds,
                  documentOptionsById,
                });
              }
              await persistTimelineItemPatch(itemId, patch);
            })
          );
          if (assigningDocuments) {
            await refreshEntityRefs();
          }
          if (assigningTags) {
            setProjectTags((prev) => {
              const merged = new Set([...prev, ...addedTags.map((tag) => tag.trim().toLowerCase())]);
              return [...merged].filter(Boolean).sort();
            });
          }
        } catch {
          setItems(previousItems);
          setEntityRefs(previousRefs);
          showErrorToast(t('timeline_assign_failed'));
        }
      };
      assignQueueRef.current = assignQueueRef.current.then(run, run);
    },
    [
      items,
      entityRefs,
      itemLinkedDocumentIds,
      documentOptionsById,
      persistTimelineItemPatch,
      refreshEntityRefs,
      showErrorToast,
      t,
    ]
  );

  const handleItemTypesChange = useCallback(
    (next: Set<string>): void => {
      if (!hasTimelineSelection) {
        setSelectedItemTypes(next);
        return;
      }
      const added = [...next].filter((type) => !assignmentItemTypes.has(type));
      if (added.length === 0) {
        return;
      }
      applyBulkTimelineAssignment({
        itemIds: selectedTimelineItemIds,
        itemType: added[added.length - 1],
      });
    },
    [
      hasTimelineSelection,
      assignmentItemTypes,
      applyBulkTimelineAssignment,
      selectedTimelineItemIds,
    ]
  );

  const handleTagsSelectionChange = useCallback(
    (next: Set<string>): void => {
      if (!hasTimelineSelection) {
        setSelectedTags(next);
        return;
      }
      const { added, removed } = diffIds([...assignmentTags], [...next]);
      applyBulkTimelineAssignment({
        itemIds: selectedTimelineItemIds,
        addedTags: added,
        removedTags: removed,
      });
    },
    [
      hasTimelineSelection,
      assignmentTags,
      applyBulkTimelineAssignment,
      selectedTimelineItemIds,
    ]
  );

  const handleDocumentsSelectionChange = useCallback(
    (next: Set<string>): void => {
      if (!hasTimelineSelection) {
        const [first] = next;
        setSelectedDocumentId(first ?? '');
        return;
      }
      const { added, removed } = diffIds([...assignmentDocumentIds], [...next]);
      applyBulkTimelineAssignment({
        itemIds: selectedTimelineItemIds,
        addedDocumentIds: added,
        removedDocumentIds: removed,
      });
    },
    [
      hasTimelineSelection,
      assignmentDocumentIds,
      applyBulkTimelineAssignment,
      selectedTimelineItemIds,
    ]
  );

  const filteredUniqueTypes = useMemo(
    () => [...new Set(filteredItems.map((i) => i.item_type))].sort(),
    [filteredItems]
  );
  const filteredUniqueTags = useMemo(
    () => [...new Set(filteredItems.flatMap((i) => i.tags))].filter(Boolean).sort(),
    [filteredItems]
  );

  const groupingTypesOptions = useMemo(
    () =>
      selectedItemTypes.size > 0
        ? filteredUniqueTypes.filter((type) => selectedItemTypes.has(type))
        : filteredUniqueTypes,
    [filteredUniqueTypes, selectedItemTypes]
  );
  const groupingTagsOptions = useMemo(
    () =>
      selectedTags.size > 0 ? filteredUniqueTags.filter((tag) => selectedTags.has(tag)) : filteredUniqueTags,
    [filteredUniqueTags, selectedTags]
  );

  const groupingDocumentsOptions = useMemo(() => {
    const referencedDocIds = new Set<string>();
    for (const docSet of itemDocumentIds.values()) {
      for (const docId of docSet) {
        referencedDocIds.add(docId);
      }
    }
    return uniqueDocuments.filter((doc) => referencedDocIds.has(doc.id));
  }, [uniqueDocuments, itemDocumentIds]);

  const { groups: visGroups, items: visItemsWithGroup } = useMemo(() => {
    if (groupBy === 'none') {
      return { groups: [] as { id: string; content: string }[], items: null };
    }
    if (groupBy === 'type' && groupByTypes.size > 0) {
      const typesOrder = groupingTypesOptions.filter((t) => groupByTypes.has(t));
      const groups = typesOrder.map((type) => ({
        id: `type:${type}`,
        content: t(ITEM_TYPE_LABEL_KEY[type] ?? type),
      }));
      const hasOther = displayItems.some((item) => !groupByTypes.has(item.item_type));
      if (hasOther) {
        groups.push({ id: 'type:__other__', content: t('timeline_group_ungrouped') });
      }
      return {
        groups,
        items: displayItems.map((item) => ({
          id: item.id,
          content: item.name,
          start: new Date(item.start_date),
          end: item.end_date ? new Date(item.end_date) : undefined,
          group: groupByTypes.has(item.item_type) ? `type:${item.item_type}` : 'type:__other__',
          ...visItemExtras(item),
        })),
      };
    }
    if (groupBy === 'tag' && groupByTags.size > 0) {
      const tagsOrder = uniqueTags.filter((t) => groupByTags.has(t));
      const groups = tagsOrder.map((tag) => ({ id: `tag:${tag}`, content: tag }));
      const items: { id: string; content: string; start: Date; end?: Date; group: string }[] = [];
      for (const item of displayItems) {
        const itemTags = item.tags.filter((tag) => groupByTags.has(tag));
        if (itemTags.length === 0) {
          items.push({
            id: item.id,
            content: truncateTimelineTitle(item.name),
            start: new Date(item.start_date),
            end: item.end_date ? new Date(item.end_date) : undefined,
            group: 'tag:__ungrouped__',
            ...visItemExtras(item),
          });
        } else {
          for (const tag of itemTags) {
            items.push({
              id: `${item.id}__${tag}`,
              content: item.name,
              start: new Date(item.start_date),
              end: item.end_date ? new Date(item.end_date) : undefined,
              group: `tag:${tag}`,
              ...visItemExtras(item),
            });
          }
        }
      }
      const ungroupedId = 'tag:__ungrouped__';
      if (items.some((i) => i.group === ungroupedId)) {
        groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      }
      return { groups, items };
    }
    if (groupBy === 'chapter') {
      const itemToChapterMap = new Map<string, string>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'timeline_item' && ref.targetType === 'manual_chapter') {
          itemToChapterMap.set(ref.sourceId, ref.targetId);
        }
        if (ref.sourceType === 'manual_chapter' && ref.targetType === 'timeline_item') {
          if (!itemToChapterMap.has(ref.targetId)) {
            itemToChapterMap.set(ref.targetId, ref.sourceId);
          }
        }
      }
      const sortedChapters = [...chapterGroups].sort(
        (a, b) => (a.documentId.localeCompare(b.documentId) || a.order - b.order)
      );
      const groups = sortedChapters.map((ch) => ({
        id: `chapter:${ch.documentId}:${ch.id}`,
        content: `${ch.documentTitle}: ${ch.title}`,
      }));
      const ungroupedId = 'chapter:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items = displayItems.map((item) => {
        const chapterId = itemToChapterMap.get(item.id);
        const chapter = chapterId ? sortedChapters.find((c) => c.id === chapterId) : null;
        const group = chapter ? `chapter:${chapter.documentId}:${chapter.id}` : ungroupedId;
        return {
          id: item.id,
          content: truncateTimelineTitle(item.name),
          start: new Date(item.start_date),
          end: item.end_date ? new Date(item.end_date) : undefined,
          group,
          ...visItemExtras(item),
        };
      });
      return { groups, items };
    }
    if (groupBy === 'storyline') {
      const itemToStorylineMap = new Map<string, string>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'timeline_item' && ref.targetType === 'storyline') {
          itemToStorylineMap.set(ref.sourceId, ref.targetId);
        }
      }
      const sortedStorylines = [...storylineGroups].sort(
        (a, b) => a.documentId.localeCompare(b.documentId) || a.title.localeCompare(b.title)
      );
      const groups = sortedStorylines.map((s) => ({
        id: `storyline:${s.documentId}:${s.id}`,
        content: `${s.documentTitle}: ${s.title}`,
      }));
      const ungroupedId = 'storyline:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items = displayItems.map((item) => {
        const storylineId = itemToStorylineMap.get(item.id);
        const storyline = storylineId ? sortedStorylines.find((s) => s.id === storylineId) : null;
        const group = storyline ? `storyline:${storyline.documentId}:${storyline.id}` : ungroupedId;
        return {
          id: item.id,
          content: truncateTimelineTitle(item.name),
          start: new Date(item.start_date),
          end: item.end_date ? new Date(item.end_date) : undefined,
          group,
          ...visItemExtras(item),
        };
      });
      return { groups, items };
    }
    if (groupBy === 'scene') {
      const itemToSceneMap = new Map<string, string>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'timeline_item' && ref.targetType === 'scene') {
          itemToSceneMap.set(ref.sourceId, ref.targetId);
        }
      }
      const sortedScenes = [...sceneGroups].sort(
        (a, b) => a.documentId.localeCompare(b.documentId) || a.order - b.order
      );
      const groups = sortedScenes.map((s) => ({
        id: `scene:${s.documentId}:${s.id}`,
        content: `${s.documentTitle}: ${s.title}`,
      }));
      const ungroupedId = 'scene:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items = displayItems.map((item) => {
        const sceneId = itemToSceneMap.get(item.id);
        const scene = sceneId ? sortedScenes.find((s) => s.id === sceneId) : null;
        const group = scene ? `scene:${scene.documentId}:${scene.id}` : ungroupedId;
        return {
          id: item.id,
          content: truncateTimelineTitle(item.name),
          start: new Date(item.start_date),
          end: item.end_date ? new Date(item.end_date) : undefined,
          group,
          ...visItemExtras(item),
        };
      });
      return { groups, items };
    }
    if (groupBy === 'document') {
      const docsToGroup =
        selectedGroupByDocuments.size > 0
          ? uniqueDocuments.filter((doc) => selectedGroupByDocuments.has(doc.id))
          : uniqueDocuments;
      const groups = docsToGroup.map((doc) => ({
        id: `document:${doc.id}`,
        content: doc.title,
      }));
      const ungroupedId = 'document:__ungrouped__';
      const items: { id: string; content: string; start: Date; end?: Date; group: string }[] = [];
      for (const item of displayItems) {
        const docIds = itemDocumentIds.get(item.id);
        const matchingDocs = docsToGroup.filter((doc) => docIds?.has(doc.id));
        if (matchingDocs.length === 0) {
          items.push({
            id: item.id,
            content: truncateTimelineTitle(item.name),
            start: new Date(item.start_date),
            end: item.end_date ? new Date(item.end_date) : undefined,
            group: ungroupedId,
            ...visItemExtras(item),
          });
        } else {
          for (const doc of matchingDocs) {
            items.push({
              id: `${item.id}__doc__${doc.id}`,
              content: truncateTimelineTitle(item.name),
              start: new Date(item.start_date),
              end: item.end_date ? new Date(item.end_date) : undefined,
              group: `document:${doc.id}`,
              ...visItemExtras(item),
            });
          }
        }
      }
      if (items.some((i) => i.group === ungroupedId)) {
        groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      }
      return { groups, items };
    }
    return { groups: [] as { id: string; content: string }[], items: null };
  }, [groupBy, groupByTypes, groupByTags, groupingTypesOptions, groupingTagsOptions, selectedGroupByDocuments, uniqueDocuments, itemDocumentIds, displayItems, entityRefs, chapterGroups, storylineGroups, sceneGroups, t]);

  const getTimelineHeightPx = useCallback((): string => {
    const frameHeight = timelineFrameRef.current?.clientHeight ?? 0;
    if (frameHeight > 0) {
      return `${Math.round(frameHeight)}px`;
    }
    return '400px';
  }, []);

  const timelineBounds = useMemo(() => {
    if (displayItems.length === 0) return null;
    const dataMin = new Date(Math.min(...displayItems.map((i) => new Date(i.start_date).getTime())));
    const dataMax = new Date(
      Math.max(
        ...displayItems.map((i) =>
          (i.end_date ? new Date(i.end_date) : new Date(i.start_date)).getTime()
        )
      )
    );
    const min = new Date(dataMin);
    min.setFullYear(min.getFullYear() - 1);
    const max = new Date(dataMax);
    max.setFullYear(max.getFullYear() + 1);
    return { min, max };
  }, [displayItems]);

  const fetchItems = useCallback(async (options?: { silent?: boolean }): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items ?? []);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [projectId, token]);

  const fetchTimelineViews = useCallback(async (): Promise<void> => {
    if (!projectId || !token) return;
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTimelineViews(
          (data.views ?? []).map((view: { id: string; name: string }) => ({ id: view.id, name: view.name }))
        );
      } else {
        setTimelineViews([]);
      }
    } catch {
      setTimelineViews([]);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    void fetchTimelineViews();
  }, [fetchTimelineViews]);

  useEffect(() => {
    const handleTimelineViewsChanged = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId === projectId) {
        void fetchTimelineViews();
      }
    };
    window.addEventListener('timeline-view-saved', handleTimelineViewsChanged);
    window.addEventListener('timeline-views-changed', handleTimelineViewsChanged);
    return () => {
      window.removeEventListener('timeline-view-saved', handleTimelineViewsChanged);
      window.removeEventListener('timeline-views-changed', handleTimelineViewsChanged);
    };
  }, [projectId, fetchTimelineViews]);

  useEffect(() => {
    const fetchEntityAndChapterData = async (): Promise<void> => {
      if (!projectId || !token) return;
      try {
        const [refsRes, entitiesRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (refsRes.ok) {
          const refsData = await refsRes.json();
          setEntityRefs(Array.isArray(refsData.references) ? refsData.references : []);
        } else {
          setEntityRefs([]);
        }
        if (entitiesRes.ok) {
          const entitiesData = await entitiesRes.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(entitiesData));
          setChapterGroups(entitiesData.chapters ?? []);
          setStorylineGroups(entitiesData.storylines ?? []);
          setSceneGroups(entitiesData.scenes ?? []);
          const docs = entitiesData.documents ?? [];
          setProjectDocuments(docs);
          setAutoExtractDocId((prev) => {
            if (prev && docs.some((d: { id: string }) => d.id === prev)) return prev;
            return docs[0]?.id ?? '';
          });
        } else {
          setMentionableEntities([]);
          setChapterGroups([]);
          setStorylineGroups([]);
          setSceneGroups([]);
          setProjectDocuments([]);
        }
      } catch {
        setEntityRefs([]);
        setMentionableEntities([]);
        setChapterGroups([]);
        setStorylineGroups([]);
        setSceneGroups([]);
        setProjectDocuments([]);
      }
    };
    fetchEntityAndChapterData();
  }, [projectId, token]);

  useEffect(() => {
    if (!projectId || !token || !autoExtractDocId) {
      setAutoFreshnessWarn(null);
      return;
    }
    let cancelled = false;
    const loadFreshness = async (): Promise<void> => {
      try {
        const response = await fetch(
          `${API_BASE}/api/projects/${projectId}/timeline-auto-freshness`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const docs = (data.freshness?.documents || []) as Array<{
          documentId: string;
          drift: string;
          freshnessPercent: number | null;
        }>;
        const drifted = docs.filter(
          (d) =>
            d.drift === 'moderate' ||
            d.drift === 'major' ||
            d.drift === 'missing_sync_metadata' ||
            d.drift === 'absent'
        );
        const selected = docs.find((d) => d.documentId === autoExtractDocId);
        if (drifted.length > 0) {
          const parts = drifted.map((d) => {
            const title =
              projectDocuments.find((doc) => doc.id === d.documentId)?.title ?? d.documentId.slice(0, 8);
            const pct = d.freshnessPercent != null ? `~${d.freshnessPercent}%` : d.drift;
            return `${title} (${pct})`;
          });
          setAutoFreshnessWarn(parts.join(', '));
        } else if (selected?.drift === 'minor' && selected.freshnessPercent != null) {
          setAutoFreshnessWarn(null);
        } else {
          setAutoFreshnessWarn(null);
        }
      } catch {
        if (!cancelled) setAutoFreshnessWarn(null);
      }
    };
    void loadFreshness();
    return () => {
      cancelled = true;
    };
  }, [projectId, token, autoExtractDocId, projectDocuments, items.length]);

  useEffect(() => {
    if (!viewIdParam) {
      setActiveTimelineViewName(null);
      setLoadedViewState(null);
      const hadLoadedView = previousTimelineViewIdRef.current != null;
      previousTimelineViewIdRef.current = null;
      if (preserveStateOnViewClearRef.current) {
        preserveStateOnViewClearRef.current = false;
        return;
      }
      if (!hadLoadedView) {
        return;
      }
      setTextFilter('');
      setSelectedItemTypes(new Set());
      setSelectedTags(new Set());
      setExcludedTags(new Set());
      setSelectedDocumentId('');
      setGroupBy('none');
      setGroupByTypes(new Set());
      setGroupByTags(new Set());
      setSelectedGroupByDocuments(new Set());
      setViewMode('timeline');
      setDetailLevel(null);
      setTimelineWindow(null);
      setSavedTimelineWindow(null);
      initialTimelineWindowRef.current = null;
      return;
    }
    if (!projectId || !token) return;
    previousTimelineViewIdRef.current = viewIdParam;
    setActiveTimelineViewName(null);
    setLoadedViewState(null);
    const loadView = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-views/${viewIdParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const viewState = data.state as TimelineViewState | undefined;
        if (typeof data.name === 'string' && data.name) {
          setActiveTimelineViewName(data.name);
        }
        if (viewState) {
          const nextTextFilter = viewState.textFilter ?? '';
          const nextItemTypes = viewState.selectedItemTypes ?? [];
          const nextTags = viewState.selectedTags ?? [];
          const nextExcludedTags = viewState.excludedTags ?? [];
          const nextDocumentId = viewState.selectedDocumentId ?? '';
          const nextGroupBy = viewState.groupBy ?? 'none';
          const nextGroupByDocuments = viewState.groupByDocuments ?? [];
          const nextGroupByTypes = viewState.groupByTypes ?? [];
          const nextGroupByTags = viewState.groupByTags ?? [];
          const nextViewMode = viewState.viewMode ?? 'timeline';
          const loadedDetailLevel = viewState.detailLevel;
          const nextDetailLevel =
            loadedDetailLevel === 'year' ||
            loadedDetailLevel === 'month' ||
            loadedDetailLevel === 'day' ||
            loadedDetailLevel === 'second'
              ? loadedDetailLevel
              : null;
          const tw = viewState.timelineWindow;
          const nextTimelineWindow =
            tw?.start && tw?.end ? { start: tw.start, end: tw.end } : undefined;

          setTextFilter(nextTextFilter);
          setSelectedItemTypes(new Set(nextItemTypes));
          setSelectedTags(new Set(nextTags));
          setExcludedTags(new Set(nextExcludedTags));
          setSelectedDocumentId(nextDocumentId);
          setGroupBy(nextGroupBy);
          setSelectedGroupByDocuments(new Set(nextGroupByDocuments));
          setGroupByTypes(new Set(nextGroupByTypes));
          setGroupByTags(new Set(nextGroupByTags));
          setViewMode(nextViewMode);
          setDetailLevel(nextDetailLevel);
          if (nextTimelineWindow) {
            setTimelineWindow(nextTimelineWindow);
            setSavedTimelineWindow(nextTimelineWindow);
            initialTimelineWindowRef.current = nextTimelineWindow;
          } else {
            setTimelineWindow(null);
            setSavedTimelineWindow(null);
            initialTimelineWindowRef.current = null;
          }
          setLoadedViewState({
            textFilter: nextTextFilter,
            selectedItemTypes: [...nextItemTypes],
            selectedTags: [...nextTags],
            excludedTags: [...nextExcludedTags],
            selectedDocumentId: nextDocumentId,
            groupBy: nextGroupBy,
            groupByTypes: [...nextGroupByTypes],
            groupByTags: [...nextGroupByTags],
            groupByDocuments: [...nextGroupByDocuments],
            viewMode: nextViewMode,
            detailLevel: nextDetailLevel,
            ...(nextTimelineWindow ? { timelineWindow: nextTimelineWindow } : {}),
          });
        }
      } catch {
        // ignore
      }
    };
    loadView();
  }, [viewIdParam, projectId, token, setViewMode]);


  useEffect(() => {
    if (!timelineItemIdParam || loading || items.length === 0) return;
    const item = items.find((i) => i.id === timelineItemIdParam);
    if (item) {
      if (canEditProject) {
        setEditItem(item);
        setIsAddModalOpen(true);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('timelineItemId');
      setSearchParams(next, { replace: true });
    }
  }, [timelineItemIdParam, loading, items, searchParams, setSearchParams, canEditProject]);

  useEffect(() => {
    if (
      !viewIdParam &&
      !timelineWindowRef.current &&
      timelineRef.current &&
      viewMode === 'timeline' &&
      timelineBounds
    ) {
      timelineRef.current.setWindow(timelineBounds.min, timelineBounds.max);
    }
  }, [viewIdParam, viewMode, timelineBounds]);

  const resolveItemId = useCallback(
    (visItemId: string): string => {
      const exact = items.find((i) => i.id === visItemId);
      if (exact) return visItemId;
      const fromSynthetic = items.find((i) => visItemId.startsWith(i.id + '__'));
      return fromSynthetic ? fromSynthetic.id : visItemId;
    },
    [items]
  );

  useEffect(() => {
    if (viewMode !== 'timeline' || loading || items.length === 0) {
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
      return;
    }
    if (filteredItems.length === 0 || !timelineBounds) {
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    let cancelled = false;
    const itemsToRender = displayItems;
    const useGroups = visGroups.length > 0 && visItemsWithGroup && visItemsWithGroup.length > 0;
    const visItems = useGroups
      ? visItemsWithGroup
      : itemsToRender.map((item) => ({
          id: item.id,
          content: truncateTimelineTitle(item.name),
          start: new Date(item.start_date),
          end: item.end_date ? new Date(item.end_date) : undefined,
          ...visItemExtras(item),
        }));
    const minDate = timelineBounds.min;
    const maxDate = timelineBounds.max;
    const preservedWindow = timelineWindowRef.current ?? initialTimelineWindowRef.current;
    const initTimeline = (): void => {
      if (cancelled || !containerRef.current) {
        return;
      }
      const el = containerRef.current;
      const tryCreate = (): void => {
        if (cancelled || !containerRef.current) return;
        const frameEl = timelineFrameRef.current;
        if (!frameEl || frameEl.offsetWidth === 0 || frameEl.offsetHeight === 0) return;
        const observer = resizeObserverRef.current;
        if (observer) {
          observer.disconnect();
          resizeObserverRef.current = null;
        }
        import('vis-timeline/standalone').then(({ Timeline: TimelineClass }) => {
          if (cancelled || !containerRef.current) return;
          const options = {
            margin: { item: 10 },
            showTooltips: false,
            height: getTimelineHeightPx(),
            min: minDate,
            max: maxDate,
          };
          const timeline = useGroups
            ? new TimelineClass(el, visItems, visGroups, options)
            : new TimelineClass(el, visItems, options);
          timelineRef.current = timeline;
          let lastHeight = options.height;
          const updateHeight = (): void => {
            if (!timelineRef.current) return;
            const nextHeight = getTimelineHeightPx();
            if (nextHeight === lastHeight) return;
            lastHeight = nextHeight;
            timelineRef.current.setOptions({ height: nextHeight });
          };
          resizeHandlerRef.current = updateHeight;
          window.addEventListener('resize', updateHeight);
          const heightObserver = new ResizeObserver(updateHeight);
          resizeObserverRef.current = heightObserver;
          if (timelineFrameRef.current) {
            heightObserver.observe(timelineFrameRef.current);
          }
          if (preservedWindow) {
            timeline.setWindow(new Date(preservedWindow.start), new Date(preservedWindow.end));
          } else {
            timeline.setWindow(minDate, maxDate);
          }
          timeline.on('rangechanged', (props: { start: Date; end: Date }) => {
            if (!cancelled) {
              setTimelineWindow({
                start: props.start.toISOString(),
                end: props.end.toISOString(),
              });
            }
          });
          timeline.on('click', (props: { item?: string | null; time?: Date; what?: string; group?: string | number | null }) => {
            const visTimeline = timeline as unknown as {
              getItemsAtCurrentTime: (time: Date) => Array<string | number>;
            };
            let visId: string | undefined =
              props.item !== undefined && props.item !== null ? String(props.item) : undefined;
            if (!visId && props.time && props.what === 'background') {
              let ids = visTimeline.getItemsAtCurrentTime(props.time).map((id) => String(id));
              if (useGroups && props.group != null && visItemsWithGroup) {
                const g = String(props.group);
                ids = ids.filter((id) => {
                  const row = visItemsWithGroup.find((v) => v.id === id);
                  return Boolean(row && 'group' in row && String(row.group) === g);
                });
              }
              const planningIds = ids.filter((id) => {
                const realId = resolveItemId(id);
                const it = items.find((i) => i.id === realId);
                return Boolean(it && isPlanningRangeItem(it));
              });
              if (planningIds.length === 1) {
                visId = planningIds[0];
              } else if (planningIds.length > 1) {
                const score = (id: string): number => {
                  const realId = resolveItemId(id);
                  const it = items.find((i) => i.id === realId);
                  if (!it?.end_date) return Infinity;
                  return new Date(it.end_date).getTime() - new Date(it.start_date).getTime();
                };
                planningIds.sort((a, b) => score(a) - score(b));
                visId = planningIds[0];
              }
            }
            if (!visId) return;
            const realId = resolveItemId(visId);
            const item = items.find((i) => i.id === realId);
            if (item && canEditProjectRef.current) {
              setEditItem(item);
              setIsAddModalOpen(true);
            }
          });
          timeline.on('itemover', (props: { item: string; event: MouseEvent }) => {
            if (hoverCloseTimeoutRef.current) {
              clearTimeout(hoverCloseTimeoutRef.current);
              hoverCloseTimeoutRef.current = null;
            }
            setHoveredItem({ itemId: props.item, position: { top: props.event.clientY, left: props.event.clientX } });
          });
          timeline.on('itemout', () => {
            hoverCloseTimeoutRef.current = setTimeout(() => {
              setHoveredItem(null);
              hoverCloseTimeoutRef.current = null;
            }, 150);
          });
        });
      };
      const frameEl = timelineFrameRef.current;
      if (frameEl && frameEl.offsetWidth > 0 && frameEl.offsetHeight > 0) {
        tryCreate();
        return;
      }
      const observer = new ResizeObserver(() => {
        if (cancelled) return;
        tryCreate();
      });
      resizeObserverRef.current = observer;
      observer.observe(frameEl ?? el);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(initTimeline);
    });
    return () => {
      cancelled = true;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = null;
      }
      setHoveredItem(null);
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
    };
  }, [viewMode, loading, items, displayItems, filteredItems, timelineBounds, getTimelineHeightPx, visGroups, visItemsWithGroup, resolveItemId]);

  const handleModalSuccess = useCallback((): void => {
    if (timelineRef.current) {
      const current = timelineRef.current.getWindow();
      const next = {
        start: current.start.toISOString(),
        end: current.end.toISOString(),
      };
      timelineWindowRef.current = next;
      setTimelineWindow(next);
    }
    void fetchItems({ silent: true });
    setIsAddModalOpen(false);
    setEditItem(null);
    setDetailScrollToSection(undefined);
  }, [fetchItems]);

  const handleModalClose = useCallback((): void => {
    setIsAddModalOpen(false);
    setEditItem(null);
    setDetailScrollToSection(undefined);
  }, []);

  const openTimelineItemDetail = useCallback(
    (itemId: string, scrollToSection?: 'related_entities'): void => {
      const fullItem = items.find((entry) => entry.id === itemId);
      if (!fullItem) {
        return;
      }
      setDetailScrollToSection(scrollToSection);
      setEditItem(fullItem);
      setIsAddModalOpen(true);
    },
    [items]
  );

  const handleTimelineItemTagsUpdated = useCallback((itemId: string, tags: string[]): void => {
    setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, tags } : item)));
  }, []);

  const getItemTypeLabel = useCallback(
    (itemType: string): string => t(ITEM_TYPE_LABEL_KEY[itemType] ?? itemType),
    [t]
  );

  const dateLocale = toBcp47Locale(locale);

  const handleTimelineViewSelect = useCallback((selectedViewId: string): void => {
    const next = new URLSearchParams(searchParams);
    if (!selectedViewId) {
      next.delete('viewId');
    } else {
      next.set('viewId', selectedViewId);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearTimelineView = useCallback((): void => {
    preserveStateOnViewClearRef.current = true;
    handleTimelineViewSelect('');
  }, [handleTimelineViewSelect]);

  const handleUpdateTimelineView = useCallback(async (): Promise<void> => {
    if (!projectId || !token || !viewIdParam || updatingTimelineView) return;
    setUpdatingTimelineView(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/timeline-views/${viewIdParam}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ state: currentViewState }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed');
      }
      setLoadedViewState(currentViewState);
      if (viewMode === 'timeline' && timelineWindow) {
        setSavedTimelineWindow(timelineWindow);
      }
      showToast(t('timeline_view_updated'));
      window.dispatchEvent(new CustomEvent('timeline-view-saved', { detail: { projectId } }));
    } catch (error) {
      console.error(error);
      showErrorToast(t('timeline_view_update_failed'));
    } finally {
      setUpdatingTimelineView(false);
    }
  }, [
    projectId,
    token,
    viewIdParam,
    updatingTimelineView,
    currentViewState,
    viewMode,
    timelineWindow,
    showToast,
    showErrorToast,
    t,
  ]);

  const handleTimelineViewActionClick = useCallback((): void => {
    if (hasLoadedTimelineView) {
      if (!isLoadedTimelineViewDirty) return;
      void handleUpdateTimelineView();
      return;
    }
    setIsSaveViewModalOpen(true);
  }, [hasLoadedTimelineView, isLoadedTimelineViewDirty, handleUpdateTimelineView]);

  const handleAutoExtract = useCallback(async (): Promise<void> => {
    if (!projectId || !token || !autoExtractDocId || autoExtracting) return;
    setAutoExtracting(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/timeline-items/auto-extract`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ documentId: autoExtractDocId }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed');
      }
      await fetchItems({ silent: true });
    } catch (error) {
      console.error(error);
      showErrorToast(t('timeline_auto_extract_failed'));
    } finally {
      setAutoExtracting(false);
    }
  }, [projectId, token, autoExtractDocId, autoExtracting, fetchItems, showErrorToast, t]);

  const timelineActions = projectId && canEditProject && shouldShowHeaderCreateActions(items.length > 0, isMobile) ? (
    <div className="flex flex-col items-end gap-1">
      {autoFreshnessWarn ? (
        <div className="text-xs text-amber-700 dark:text-amber-400 max-w-md text-right">
          {t('timeline_auto_freshness_warning').replace('{details}', autoFreshnessWarn)}
        </div>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {isMobile ? (
          <AddTimelineEventDataMenu
            onAddEvent={() => setIsAddModalOpen(true)}
            onImport={() => setIsImportModalOpen(true)}
            onFromDocument={() => setAutoExtractConfirmOpen(true)}
            fromDocumentDisabled={projectDocuments.length === 0}
            fromDocumentLoading={autoExtracting}
          />
        ) : (
          <>
            <AddItemButton
              onClick={() => setIsAddModalOpen(true)}
              label={t('add_event')}
              tooltip={t('add_event_tooltip')}
            />
            <AddItemButton
              onClick={() => setIsImportModalOpen(true)}
              label={t('timeline_import')}
              tooltip={t('timeline_import_tooltip')}
            />
            <AddItemButton
              onClick={() => setAutoExtractConfirmOpen(true)}
              label={t('timeline_auto_extract')}
              loading={autoExtracting}
              loadingLabel={t('analyzing')}
              disabled={projectDocuments.length === 0}
              tooltip={t('timeline_auto_extract_tooltip')}
            />
          </>
        )}
      </div>
    </div>
  ) : undefined;

  const panelTitle =
    viewIdParam && activeTimelineViewName
      ? `${t('Timeline')}: ${activeTimelineViewName}`
      : t('Timeline');

  const renderDetailLevelControl = (inputClassName: string): React.ReactElement => (
    <SingleSelectCombobox
      options={[
        { value: TIMELINE_DETAIL_LEVEL_UNSET, label: t('timeline_detail_level_unset') },
        { value: 'year', label: t('timeline_precision_year') },
        { value: 'month', label: t('timeline_precision_month') },
        { value: 'day', label: t('timeline_precision_day') },
        { value: 'second', label: t('timeline_precision_datetime') },
      ]}
      value={detailLevel ?? TIMELINE_DETAIL_LEVEL_UNSET}
      onValueChange={(value) => {
        if (
          value === 'year' ||
          value === 'month' ||
          value === 'day' ||
          value === 'second'
        ) {
          setDetailLevel(value);
          return;
        }
        setDetailLevel(null);
      }}
      placeholder={t('timeline_detail_level')}
      searchPlaceholder={t('timeline_filter_search_placeholder')}
      emptyMessage={t('timeline_filter_no_results')}
      align="end"
      inputClassName={inputClassName}
    />
  );

  const renderTimelineViewSaveControls = (): React.ReactNode => {
    if (!(timelineViews.length > 0 || canEditProject)) return null;
    return (
      <div
        className={cn(
          'inline-flex min-w-0 max-w-full items-stretch',
          timelineViews.length > 0 &&
            canEditProject &&
            'h-9 overflow-hidden rounded-md border border-[rgba(39,39,41,0.4)] bg-white dark:border-input dark:bg-transparent'
        )}
      >
        {timelineViews.length > 0 && (
          <SingleSelectCombobox
            options={timelineViewOptions}
            value={viewIdParam ?? ''}
            onValueChange={handleTimelineViewSelect}
            placeholder={t('timeline_select_view')}
            searchPlaceholder={t('timeline_filter_search_placeholder')}
            emptyMessage={t('timeline_filter_no_results')}
            align="start"
            clearable={hasLoadedTimelineView}
            onClear={handleClearTimelineView}
            clearAriaLabel={t('timeline_clear_view')}
            inputClassName={cn(
              'min-w-0 w-[min(100%,14rem)] sm:min-w-[14rem] sm:w-auto',
              canEditProject && 'h-full rounded-none border-0 bg-transparent shadow-none focus-within:ring-0'
            )}
          />
        )}
        {canEditProject && (
          <TooltipProvider delayDuration={0}>
            <ButtonTooltip
              tooltip={
                hasLoadedTimelineView
                  ? t('timeline_update_view_tooltip')
                  : t('timeline_save_view_tooltip')
              }
              side="bottom"
            >
              <span className="inline-flex h-full shrink-0">
                <Button
                  variant={timelineViews.length > 0 ? 'ghost' : 'outline'}
                  size="sm"
                  disabled={
                    hasLoadedTimelineView
                      ? !isLoadedTimelineViewDirty || updatingTimelineView
                      : !canSaveView
                  }
                  onClick={handleTimelineViewActionClick}
                  className={cn(
                    'group h-9 shrink-0',
                    timelineViews.length > 0 &&
                      'h-full rounded-none border-0 border-l border-[rgba(39,39,41,0.4)] hover:bg-[#E9E9EA] dark:border-input dark:hover:bg-accent'
                  )}
                >
                  <ListFilterPlus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                  {hasLoadedTimelineView
                    ? t('timeline_update_view')
                    : t('timeline_save_view')}
                </Button>
              </span>
            </ButtonTooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const renderTimelineViewActions = (
    className = '',
    options: { includeDetailLevel?: boolean } = {}
  ): React.ReactElement => {
    const { includeDetailLevel = true } = options;
    return (
      <div className={`flex min-w-0 max-w-full flex-wrap items-center gap-2${className ? ` ${className}` : ''}`}>
        {includeDetailLevel
          ? renderDetailLevelControl('min-w-0 w-[min(100%,10rem)] sm:min-w-[10rem] sm:w-auto')
          : null}
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
          <ViewSwitcher
            modes={['timeline', 'table']}
            ariaLabelForMode={(mode) => (mode === 'timeline' ? t('Timeline') : t('table'))}
          />
        </Tabs>
      </div>
    );
  };

  const renderTimelineFilterControls = (): React.ReactElement => (
    <>
        <Input
          type="text"
          placeholder={t('timeline_filter_by_text_placeholder')}
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          className="h-9 min-w-0 w-full xl:w-auto xl:max-w-xs"
        />
        <MultiSelectCombobox
          options={(hasTimelineSelection ? TIMELINE_ITEM_TYPE_VALUES : uniqueTypes).map((type) => ({
            value: type,
            label: t(ITEM_TYPE_LABEL_KEY[type] ?? type),
            swatchColor: timelineItemTypeSwatchColor(type),
          }))}
          selected={assignmentItemTypes}
          onSelectionChange={handleItemTypesChange}
          label={hasTimelineSelection ? t('assign_type') : t('timeline_item_type')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          clearAllLabel={t('timeline_clear_all')}
          align="start"
          triggerClassName={`xl:w-auto${assignModeClassName ? ` ${assignModeClassName}` : ''}`}
        />
        <MultiSelectCombobox
          options={[
            ...new Set([
              ...uniqueTags,
              ...(hasTimelineSelection ? projectTags : []),
              ...assignmentTags,
              ...(hasTimelineSelection ? [] : [...excludedTags]),
            ]),
          ]
            .filter(Boolean)
            .sort()
            .map((tag) => ({ value: tag, label: tag }))}
          selected={assignmentTags}
          onSelectionChange={handleTagsSelectionChange}
          excluded={hasTimelineSelection ? undefined : excludedTags}
          onExcludedChange={hasTimelineSelection ? undefined : setExcludedTags}
          allowExclude={!hasTimelineSelection}
          label={hasTimelineSelection ? t('assign_tags') : t('timeline_filter_by_tags')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          clearAllLabel={t('timeline_clear_all')}
          includeExcludeHint={t('filter_include_exclude_hint')}
          createLabel={
            hasTimelineSelection ? (tag) => `${t('Create tag')} ${tag}` : undefined
          }
          align="start"
          triggerClassName={`xl:w-auto${assignModeClassName ? ` ${assignModeClassName}` : ''}`}
        />
        {(uniqueDocuments.length > 1 || hasTimelineSelection) && (
          hasTimelineSelection ? (
            <MultiSelectCombobox
              options={uniqueDocuments.map((doc) => ({ value: doc.id, label: doc.title }))}
              selected={assignmentDocumentIds}
              onSelectionChange={handleDocumentsSelectionChange}
              label={t('assign_documents')}
              searchPlaceholder={t('timeline_filter_search_placeholder')}
              emptyMessage={t('timeline_filter_no_results')}
              clearAllLabel={t('timeline_clear_all')}
              align="start"
              triggerClassName={`xl:w-auto${assignModeClassName ? ` ${assignModeClassName}` : ''}`}
            />
          ) : (
            <>
              <SingleSelectCombobox
                options={[
                  { value: '', label: t('timeline_filter_all_documents') },
                  ...uniqueDocuments.map((doc) => ({ value: doc.id, label: doc.title })),
                ]}
                value={selectedDocumentId}
                onValueChange={setSelectedDocumentId}
                placeholder={t('timeline_filter_by_document')}
                searchPlaceholder={t('timeline_filter_search_placeholder')}
                emptyMessage={t('timeline_filter_no_results')}
                align="start"
                inputClassName="xl:w-auto"
              />
              {selectedDocumentId && (
                <button
                  type="button"
                  aria-label={t('remove')}
                  className="rounded-full hover:bg-muted p-1 shrink-0"
                  onClick={() => setSelectedDocumentId('')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </>
          )
        )}
        <DataTableSelectionActions
          selectedCount={hasTimelineSelection ? selectedTimelineItemIds.length : 0}
          countLabel={t('resources_selected_count').replace(
            '{count}',
            String(selectedTimelineItemIds.length)
          )}
          onClear={() => setRowSelection({})}
          onDelete={canEditProject ? requestDeleteSelectedTimelineItems : undefined}
          deleting={bulkDeleting}
        />
        {!hasTimelineSelection && (
          <>
        <SingleSelectCombobox
          options={[
            { value: 'none', label: t('timeline_group_by_none') },
            { value: 'type', label: t('timeline_group_by_type') },
            { value: 'tag', label: t('timeline_group_by_tag') },
            { value: 'chapter', label: t('timeline_group_by_chapter') },
            { value: 'storyline', label: t('timeline_group_by_storyline') },
            { value: 'scene', label: t('timeline_group_by_scene') },
            ...(uniqueDocuments.length > 0
              ? [{ value: 'document', label: t('timeline_group_by_document') }]
              : []),
          ]}
          value={groupBy}
          onValueChange={(v) => {
            const mode = v as GroupByMode;
            setGroupBy(mode);
            if (mode === 'type') setGroupByTypes(new Set(groupingTypesOptions));
            if (mode === 'tag') setGroupByTags(new Set(groupingTagsOptions));
            if (mode === 'document') setSelectedGroupByDocuments(new Set(uniqueDocuments.map((d) => d.id)));
            if (mode === 'chapter' || mode === 'none') {
              setGroupByTypes(new Set());
              setGroupByTags(new Set());
              setSelectedGroupByDocuments(new Set());
            }
          }}
          placeholder={t('timeline_group_by')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          align="end"
          inputClassName="xl:w-auto"
        />
        {groupBy === 'type' && (
          <MultiSelectCombobox
            options={filteredUniqueTypes.map((type) => ({
              value: type,
              label: t(ITEM_TYPE_LABEL_KEY[type] ?? type),
            }))}
            selected={groupByTypes}
            onSelectionChange={setGroupByTypes}
            label={t('timeline_group_by_types_label')}
            searchPlaceholder={t('timeline_filter_search_placeholder')}
            emptyMessage={t('timeline_filter_no_results')}
            clearAllLabel={t('timeline_clear_all')}
            align="end"
            triggerClassName="xl:w-auto"
          />
        )}
        {groupBy === 'tag' && (
          <MultiSelectCombobox
            options={groupingTagsOptions.map((tag) => ({ value: tag, label: tag }))}
            selected={groupByTags}
            onSelectionChange={setGroupByTags}
            label={t('timeline_group_by_tags_label')}
            searchPlaceholder={t('timeline_filter_search_placeholder')}
            emptyMessage={t('timeline_filter_no_results')}
            clearAllLabel={t('timeline_clear_all')}
            align="end"
            triggerClassName="xl:w-auto"
          />
        )}
        {groupBy === 'document' && uniqueDocuments.length > 1 && (
          <MultiSelectCombobox
            options={uniqueDocuments.map((doc) => ({ value: doc.id, label: doc.title }))}
            selected={selectedGroupByDocuments}
            onSelectionChange={setSelectedGroupByDocuments}
            label={t('timeline_group_by_documents_label')}
            searchPlaceholder={t('timeline_filter_search_placeholder')}
            emptyMessage={t('timeline_filter_no_results')}
            clearAllLabel={t('timeline_clear_all')}
            align="end"
            triggerClassName="xl:w-auto"
          />
        )}
          </>
        )}
    </>
  );

  if (loading && items.length === 0) {
    return (
      <ProjectPanelLayout title={panelTitle} titleIcon={PROJECT_SECTION_ICONS.timeline} actions={timelineActions} loading />
    );
  }

  return (
    <>
      <ProjectPanelLayout
        title={panelTitle}
        titleIcon={PROJECT_SECTION_ICONS.timeline}
        actions={timelineActions}
      >
        {items.length === 0 ? (
          <TimelineEmptyState
            canEdit={Boolean(canEditProject)}
            onAdd={() => setIsAddModalOpen(true)}
            onImport={() => setIsImportModalOpen(true)}
          />
        ) : (
        <div
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="mb-2 min-w-0 shrink-0 md:mb-4">
            <Collapsible
              open={isMobileFiltersOpen}
              onOpenChange={setIsMobileFiltersOpen}
              className="xl:hidden"
            >
              <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
                {renderTimelineViewSaveControls()}
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="h-9 shrink-0">
                    <ListFilter className="h-4 w-4" />
                    {t('filters')}
                    {activeFilterCount > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
                        {activeFilterCount}
                      </span>
                    )}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isMobileFiltersOpen ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <ClearFiltersButton enabled={activeFilterCount > 0} onClick={clearFilters} />
                <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
                  {renderTimelineViewActions('', { includeDetailLevel: false })}
                </div>
              </div>
              <CollapsibleContent>
                <div className="mt-3 flex flex-col gap-3">
                  {renderTimelineFilterControls()}
                  {renderDetailLevelControl('w-full')}
                </div>
              </CollapsibleContent>
            </Collapsible>
            <div className="hidden min-w-0 xl:flex xl:flex-wrap xl:items-center xl:gap-3">
              {renderTimelineViewSaveControls()}
              {renderTimelineFilterControls()}
              <ClearFiltersButton enabled={activeFilterCount > 0} onClick={clearFilters} />
              {renderTimelineViewActions('ml-auto')}
            </div>
          </div>
              {filteredItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {t('timeline_no_matches')}
                </div>
              ) : viewMode === 'timeline' ? (
                <div className="relative mt-0 flex min-h-0 flex-1 flex-col">
                  <div
                    ref={timelineFrameRef}
                    className="scribane-timeline scribane-timeline--project min-h-0 flex-1 overflow-hidden"
                  >
                    <div ref={containerRef} className="h-full w-full" />
                  </div>
                  <TimelineNavigationControls
                    timelineRef={timelineRef}
                    visibleWindow={timelineWindow}
                    bounds={timelineBounds}
                    overlay
                  />
                </div>
              ) : (
                <div className="mt-0 flex min-h-0 flex-1 flex-col">
                  <TimelineItemsTable
                    items={tableItems}
                    projectId={projectId ?? ''}
                    canEditProject={Boolean(canEditProject)}
                    itemTypeLabel={getItemTypeLabel}
                    onRowClick={(item) => openTimelineItemDetail(item.id)}
                    onSeeAllReferencedIn={(item) =>
                      openTimelineItemDetail(item.id, 'related_entities')
                    }
                    onTagsUpdated={handleTimelineItemTagsUpdated}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                  />
                </div>
              )}
              {viewMode === 'timeline' && hoveredItem && (() => {
                const item = displayItems.find((i) => i.id === resolveItemId(hoveredItem.itemId));
                if (!item) return null;
                const dateLine =
                  item.end_date && item.end_precision
                    ? `${formatTimelineDate(item.start_date, item.start_precision, dateLocale, item.start_timezone)} – ${formatTimelineDate(item.end_date, item.end_precision, dateLocale, item.end_timezone)}`
                    : formatTimelineDate(item.start_date, item.start_precision, dateLocale, item.start_timezone);
                const typeColor = timelineItemTypeSwatchColor(item.item_type);
                return (
                  <HoverCard open={true}>
                    <HoverCardTrigger asChild>
                      <div
                        style={{
                          position: 'fixed',
                          top: hoveredItem.position.top,
                          left: hoveredItem.position.left,
                          width: 1,
                          height: 1,
                          pointerEvents: 'none',
                          opacity: 0,
                          zIndex: -1,
                        }}
                      />
                    </HoverCardTrigger>
                    <HoverCardContent
                      className={`timeline-hover-card ${timelineItemTypeClassName(item.item_type)} max-w-2xl w-auto px-4 py-2 shadow-none`}
                      side="bottom"
                      align="center"
                      sideOffset={8}
                      collisionPadding={8}
                      onPointerEnter={() => {
                        if (hoverCloseTimeoutRef.current) {
                          clearTimeout(hoverCloseTimeoutRef.current);
                          hoverCloseTimeoutRef.current = null;
                        }
                      }}
                      onPointerLeave={() => {
                        hoverCloseTimeoutRef.current = setTimeout(() => {
                          setHoveredItem(null);
                          hoverCloseTimeoutRef.current = null;
                        }, 150);
                      }}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-xs font-semibold text-muted-foreground">{dateLine}</p>
                          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
                            {typeColor ? (
                              <span
                                className="size-2 rounded-full"
                                style={{ backgroundColor: typeColor }}
                                aria-hidden
                              />
                            ) : null}
                            {getItemTypeLabel(item.item_type)}
                          </span>
                        </div>
                        <p className="font-semibold leading-tight">{item.name}</p>
                        {item.description?.trim() ? (
                          <div
                            className={`${RICH_TEXT_HTML_CLASS} text-muted-foreground [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0`}
                            dangerouslySetInnerHTML={{ __html: item.description.trim() }}
                          />
                        ) : null}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );
              })()}
        </div>
        )}
      </ProjectPanelLayout>
      {projectId && (
        <>
          <ImportTimelineModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            projectId={projectId}
            onSuccess={() => {
              fetchItems();
              setIsImportModalOpen(false);
            }}
          />
          <SaveTimelineViewModal
            isOpen={isSaveViewModalOpen}
            onClose={() => setIsSaveViewModalOpen(false)}
            projectId={projectId}
            state={currentViewState}
            onSuccess={() => {
              if (viewMode === 'timeline' && timelineWindow) {
                setSavedTimelineWindow(timelineWindow);
              }
              setIsSaveViewModalOpen(false);
              window.dispatchEvent(new CustomEvent('timeline-view-saved', { detail: { projectId } }));
            }}
          />
          <AddTimelineEventModal
            isOpen={isAddModalOpen}
            onClose={handleModalClose}
            projectId={projectId}
            onSuccess={handleModalSuccess}
            scrollToSection={detailScrollToSection}
            initialItem={
              editItem
                ? {
                    ...editItem,
                    item_type: editItem.item_type as 'historical_event' | 'fictional_event' | 'planning' | 'other',
                  }
                : undefined
            }
            defaultsForNewItem={
              editItem || !lastEventForDefaults
                ? undefined
                : {
                    item_type: lastEventForDefaults.item_type as 'historical_event' | 'fictional_event' | 'planning' | 'other',
                    tags: lastEventForDefaults.tags,
                    start_date: lastEventForDefaults.start_date,
                    start_precision: lastEventForDefaults.start_precision,
                    start_timezone: lastEventForDefaults.start_timezone ?? null,
                    end_date: lastEventForDefaults.end_date,
                    end_precision: lastEventForDefaults.end_precision,
                    end_timezone: lastEventForDefaults.end_timezone ?? null,
                  }
            }
          />
        </>
      )}
      <ConfirmDialog
        open={autoExtractConfirmOpen}
        onOpenChange={setAutoExtractConfirmOpen}
        title={t('timeline_auto_extract_confirm_title')}
        description={t('timeline_auto_extract_confirm_description').replace(
          '{document}',
          projectDocuments.find((doc) => doc.id === autoExtractDocId)?.title ||
            t('timeline_auto_extract')
        )}
        confirmLabel={t('timeline_auto_extract')}
        destructive={false}
        confirmAsAnalyze
        confirmLoading={autoExtracting}
        confirmDisabled={!autoExtractDocId}
        onConfirm={handleAutoExtract}
      >
        <div className="space-y-2">
          <Label title={t('timeline_auto_extract_document_tooltip')}>
            {t('timeline_auto_extract_document')}
          </Label>
          <SingleSelectCombobox
            options={projectDocuments.map((doc) => ({ value: doc.id, label: doc.title }))}
            value={autoExtractDocId}
            onValueChange={setAutoExtractDocId}
            placeholder={t('timeline_auto_extract_document')}
            searchPlaceholder={t('timeline_filter_search_placeholder')}
            emptyMessage={t('timeline_filter_no_results')}
            contentClassName="z-[2200]"
          />
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        open={pendingDeleteTimelineItemIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteTimelineItemIds([]);
          }
        }}
        title={
          pendingDeleteTimelineItemIds.length === 1
            ? t('delete_timeline_item')
            : t('delete_timeline_items')
        }
        description={
          pendingDeleteTimelineItemIds.length === 1
            ? t('confirm_delete_timeline_item')
            : t('confirm_delete_timeline_items').replace(
                '{count}',
                String(pendingDeleteTimelineItemIds.length)
              )
        }
        confirmLabel={
          pendingDeleteTimelineItemIds.length === 1
            ? t('delete_timeline_item')
            : t('delete')
        }
        onConfirm={() => {
          void confirmDeleteSelectedTimelineItems();
        }}
      />
    </>
  );
};
